// SCIM OAuth client admin client (PB-4543 / PPL-10).
//
//   POST /api/auth/admin/scim-oauth-clients                              → generate
//   GET  /api/auth/admin/scim-oauth-clients                              → cursor-paginated list
//   POST /api/auth/admin/scim-oauth-clients/{id}/rotate-secret           → rotate
//   POST /api/auth/admin/scim-oauth-clients/{id}/expire-previous-secret  → end an overlap
//   POST /api/auth/admin/scim-oauth-clients/{id}/revoke                  → revoke
//
// Mirrors services/auth/src/admin/dto/scim-oauth-client.response.ts.
//
// The panel's own tests mock this module, so nothing else exercises what it puts on
// the wire — and the wire is where this surface's contracts live: the label trim the
// column CHECK requires, the ABSENCE of any secret on a request body, the scope
// default, the grace ladder, and the tenant-scoped query keys that stop a session swap
// serving one tenant's clients to the next administrator.
//
// Exactly ONE type carries a secret: `RegisteredScimOAuthClient.clientSecret`, returned
// by generate and by rotate and by nothing else. PlaybookAI generates the value and
// keeps only a SHA-256 digest, so it is show-once and cannot be read back — which is
// why the panel carries session and clipboard fences. Every other type here has no
// field a secret could occupy.

import { AdminApiError, request } from "./admin-users-api.js";
export type ScimCredentialScope = "users" | "users_and_groups";

/** The whole stored lifecycle — a client does not expire. Rotation replaces the
    SECRET in place and keeps the client id, so there is no repoint-and-revoke dance:
    see `rotateScimOAuthClientSecret`. */
export type ScimOAuthClientStatus = "active" | "revoked";

export interface ScimOAuthClient {
  clientId: string;
  label: string;
  status: ScimOAuthClientStatus;
  /** The SAME vocabulary the withdrawn static credential used, deliberately — one
      authorization concept rather than a second one invented here. */
  scope: ScimCredentialScope;
  createdAt: string;
  /** Null until the identity provider first authenticates with it. */
  lastUsedAt: string | null;
  revokedAt: string | null;
  /** Which secret this client currently holds. Null only for a row written before
      the vendor-issued redesign — shown as unknown rather than invented. */
  secretFingerprint: string | null;
  /** When the live secret last replaced a predecessor. */
  secretRotatedAt: string | null;
  /** While a rotation overlap is open, when the PREVIOUS secret stops being
      accepted. Surfaced because an overlap nobody can see is one nobody closes. */
  previousSecretExpiresAt: string | null;
}

export interface ScimOAuthClientPage {
  clients: ScimOAuthClient[];
  nextCursor: string | null;
}

export interface RegisteredScimOAuthClient {
  client: ScimOAuthClient;
  /** The generated secret. Returned by issue and rotate and by NOTHING else: the
      server stores only a SHA-256 digest, so once this response is gone the value
      is unrecoverable and the only remedy is another rotation. */
  clientSecret: string;
  /** 8-character non-reversible checksum of that secret. Also stored, so the list
      can say which secret a provider holds. */
  secretFingerprint: string;
  /** Where Entra exchanges the client credentials for an access token. */
  tokenEndpoint: string;
  /** The tenant URL Entra provisions against. */
  scimBaseUrl: string;
}

export { AdminApiError };

/* Matches the endpoint's MAX_LIMIT: it 400s above this rather than clamping. */
const CLIENT_PAGE_SIZE = 50;

/** Mirrors LABEL_MAX in the auth service and the column CHECK. */
const LABEL_MAX = 255;

export type ScimOAuthClientStatusFilter = ScimOAuthClientStatus;

/**
 * The tenant-scoped PREFIX of every OAuth client key.
 *
 * Tenant-scoped so a session swap cannot serve another tenant's registrations
 * out of the cache, and invalidated by prefix so every filter the admin has
 * visited is cleared — the same reasoning as `scimCredentialsQueryKeyPrefix`.
 */
export function scimOAuthClientsQueryKeyPrefix(
  tenantId: string | null | undefined,
): readonly unknown[] {
  return ["auth", "admin", "scim-oauth-clients", tenantId ?? "none"];
}

export function scimOAuthClientsQueryKey(
  tenantId: string | null | undefined,
  filters: { readonly status?: ScimOAuthClientStatusFilter } = {},
): readonly unknown[] {
  return [...scimOAuthClientsQueryKeyPrefix(tenantId), filters.status ?? "all"];
}

export async function listScimOAuthClients(
  cursor?: string | null,
  filters: { readonly status?: ScimOAuthClientStatusFilter } = {},
): Promise<ScimOAuthClientPage> {
  const params = new URLSearchParams({ limit: String(CLIENT_PAGE_SIZE) });
  if (cursor) params.set("cursor", cursor);
  if (filters.status) params.set("status", filters.status);
  return (await request(
    `/api/auth/admin/scim-oauth-clients?${params.toString()}`,
  )) as ScimOAuthClientPage;
}

export async function registerScimOAuthClient(input: {
  label: string;
  scope?: ScimCredentialScope;
}): Promise<RegisteredScimOAuthClient> {
  return (await request("/api/auth/admin/scim-oauth-clients", {
    method: "POST",
    body: JSON.stringify({
      /* Label trimmed at this boundary because the server's CHECK requires
         `label = btrim(label)`. There is nothing else to send: the secret is
         generated by the server, so no request from this client ever carries a
         credential. */
      label: input.label.trim(),
      scope: input.scope ?? "users_and_groups",
    }),
  })) as RegisteredScimOAuthClient;
}

/** Grace windows a rotation may choose, in hours. Mirrors the server's ladder —
    the server stays authoritative and refuses anything not on it. */
export const GRACE_HOURS_OPTIONS = [0, 1, 12, 24, 72, 168] as const;

export const GRACE_HOURS_DEFAULT = 24;

/**
 * Replace the live secret, keeping the previous one valid for `graceHours`.
 *
 * The window is the operator's choice, not ours: only they know when their identity
 * provider will be updated. Zero is the right answer for a suspected leak.
 */
export async function rotateScimOAuthClientSecret(
  clientId: string,
  graceHours: number,
): Promise<RegisteredScimOAuthClient> {
  return (await request(
    `/api/auth/admin/scim-oauth-clients/${encodeURIComponent(clientId)}/rotate-secret`,
    { method: "POST", body: JSON.stringify({ graceHours }) },
  )) as RegisteredScimOAuthClient;
}

/** Close a rotation overlap immediately. */
export async function expireScimOAuthClientPreviousSecret(
  clientId: string,
): Promise<ScimOAuthClient> {
  return (await request(
    `/api/auth/admin/scim-oauth-clients/${encodeURIComponent(clientId)}/expire-previous-secret`,
    { method: "POST" },
  )) as ScimOAuthClient;
}

export async function revokeScimOAuthClient(
  clientId: string,
): Promise<ScimOAuthClient> {
  return (await request(
    `/api/auth/admin/scim-oauth-clients/${encodeURIComponent(clientId)}/revoke`,
    { method: "POST" },
  )) as ScimOAuthClient;
}

/**
 * Client-side pre-check for the label. The server stays authoritative.
 *
 * Bounded on the TRIMMED value, and that is deliberate rather than a mismatch
 * with the server's `parseLabel`, which bounds its raw input: `registerScimOAuthClient`
 * sends `label.trim()`, so the trimmed form IS the server's raw input. Checking
 * the untrimmed length here would refuse a label the server accepts. Same
 * reasoning, and same wording, as `validateScimCredentialLabel`.
 */
export function validateScimOAuthClientLabel(value: string): string | null {
  const trimmed = value.trim();
  if (trimmed.length === 0) return "Enter a name for this client.";
  if (trimmed.length > LABEL_MAX) {
    return `Name must be ${LABEL_MAX} characters or fewer.`;
  }
  return null;
}
